import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import Breadcrump from "../../components/Breadcrump";
import productAPI from "../../api/productApi";
import ProductCard from "./Card";
import Filtersidebar from "./detailfeatures/Filtersidebar";
import { FiFilter, FiX } from "react-icons/fi";
import { CategoryContext } from "../../context/CategoryContext";

function ListPage() {
  // Full category list (GET /categories, paged through in CategoryContext)
  // — every category, not just the navbar-curated subset — used for the
  // sidebar's Category filter.
  const { allCategories: categories } = useContext(CategoryContext);

  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filterData, setFilterData] = useState({});

  const [priceRange, setPriceRange] = useState([100, 10100]);
  const [page, setPage] = useState(1);

  const [filters, setFilters] = useState({
    category: [],
    brand: [],
    sort: "",
  });

  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);

  const debounceRef = useRef(null);
  const observer = useRef();

  const fetchProducts = async (currentPage, filtersToUse = filters) => {
    setLoading(true);

    try {
      const res = await productAPI.listing({
        page: currentPage,
        ...filtersToUse,
      });

      if (res.data) {
        const { products, pagination } = res.data;

        setItems((prev) =>
          currentPage === 1 ? products || [] : [...prev, ...(products || [])],
        );
        setTotalCount(pagination?.totalCount || 0);
      }
    } catch (err) {
      console.error("Error loading products:", err);
    } finally {
      setLoading(false);
    }
  };

  // Single source of truth for fetching products — page changes (infinite
  // scroll) OR filters changes (checkbox/sort/price) both land here, and
  // ONLY here. Filtering is instant (no separate "Apply" gate needed for
  // it to take effect) — this is the one and only place that calls the API.
  useEffect(() => {
    fetchProducts(page, filters);
  }, [page, filters]);

  const fetchFilters = async () => {
    try {
      const res = await productAPI.filters();

      if (res.data) {
        setFilterData(res.data);
        if (res.data?.priceRange) {
          setPriceRange([res.data.priceRange.min, res.data.priceRange.max]);
        }
      }
    } catch (err) {
      console.error("Filter API error:", err);
    }
  };

  useEffect(() => {
    fetchFilters();
  }, []);

  const lastItemRef = useCallback(
    (node) => {
      if (loading) return;

      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && items.length < totalCount) {
          setPage((prev) => prev + 1);
        }
      });

      if (node) observer.current.observe(node);
    },
    [loading, items, totalCount],
  );

  // Instant: sort dropdown etc. change and refetch straightaway.
  const handleFilterChange = (key, value) => {
    setItems([]);
    setPage(1);

    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  // Instant: category/brand checkbox toggles and refetches straightaway.
  const handleCheckboxChange = (key, value) => {
    setItems([]);
    setPage(1);

    setFilters((prev) => {
      const current = prev[key] || [];
      const exists = current.includes(value);

      return {
        ...prev,
        [key]: exists
          ? current.filter((item) => item !== value)
          : [...current, value],
      };
    });
  };

  // Slider moves instantly (UI), but the actual filter/fetch is debounced
  // 500ms after dragging stops — avoids firing a request per pixel.
  const handlePriceChange = (range) => {
    setPriceRange(range);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      setItems([]);
      setPage(1);

      setFilters((prev) => ({
        ...prev,
        minPrice: range[0],
        maxPrice: range[1],
      }));
    }, 500);
  };

  // Mobile only: filters are already applied instantly as they're picked
  // (see handlers above) — this just flushes a pending price-slider
  // debounce immediately and closes the drawer.
  const handleApplyFilters = () => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    setItems([]);
    setPage(1);

    setFilters((prev) => ({
      ...prev,
      minPrice: priceRange[0],
      maxPrice: priceRange[1],
    }));

    setIsMobileFilterOpen(false);
  };

  return (
    <>
      <Breadcrump title="Products" />
    <div className="min-h-screen bg-[#FFFDF8] text-[#2E211A]">
      {/* <div className="bg-[#FFFFFF] border-b border-[#E5D9C3] shadow-[0_1px_3px_rgba(74,47,30,0.12)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        
        </div>
      </div> */}

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col md:flex-row gap-8">
          {/* Desktop Filter Sidebar */}
          <aside className="hidden md:block w-72 shrink-0">
            <div className="sticky top-24  rounded-[16px]   p-6 transition-all duration-300 ">
              <Filtersidebar
                filterData={filterData}
                categories={categories}
                filters={filters}
                onFilterChange={handleFilterChange}
                onCheckboxChange={handleCheckboxChange}
                priceRange={priceRange}
                onPriceChange={handlePriceChange}
                onApply={handleApplyFilters}
              />
            </div>
          </aside>

          {/* Main Content & Products Grid */}
          <main className="flex-1">
            {/* Mobile Filter Trigger & Counter Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6  p-4 rounded-[16px] border border-[#E5D9C3] shadow-[0_1px_3px_rgba(74,47,30,0.12)]">
              <div className="flex items-center justify-between w-full">
                <span className="text-sm font-medium text-[#6B5D53]">
                  Showing <strong className="text-[#2E211A]">{items.length}</strong> of <strong className="text-[#2E211A]">{totalCount}</strong> products
                </span>
                
                <button
                  onClick={() => setIsMobileFilterOpen(true)}
                  className="md:hidden inline-flex items-center gap-2 px-4 py-2.5 bg-[#A6472E] text-white text-sm font-medium rounded-[8px] shadow-[0_4px_12px_rgba(74,47,30,0.16)] active:scale-95 transition-all duration-150"
                >
                  <FiFilter size={16} /> Filters
                </button>
              </div>
            </div>

            {/* Products Grid with Smooth Stagger/Fade Animation */}
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {items.map((item, index) => {
                const isLast = index === items.length - 1;
                return (
                  <div
                    ref={isLast ? lastItemRef : null}
                    key={item._id || item.id}
                    className="transform transition-all duration-300 "
                  >
                    <div className="h-full rounded-[16px]   overflow-hidden  transition-all duration-300 flex flex-col">
                      <ProductCard item={item} />
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Empty State */}
            {!loading && items.length === 0 && (
              <div className="text-center py-16 bg-[#FFFFFF] rounded-[16px] border border-[#E5D9C3] shadow-[0_1px_3px_rgba(74,47,30,0.12)] mt-4">
                <div className="w-16 h-16 mx-auto mb-4 bg-[#F6EFE2] text-[#A6472E] rounded-full flex items-center justify-center text-2xl">
                  📦
                </div>
                <h3 className="text-lg font-semibold text-[#2E211A] mb-1">No products found</h3>
                <p className="text-sm text-[#6B5D53]">Try adjusting your filters or search criteria.</p>
              </div>
            )}

            {/* Loading Indicator */}
            {loading && (
              <div className="flex flex-col items-center justify-center py-12 gap-3">
                <div className="w-8 h-8 border-3 border-[#A6472E] border-t-transparent rounded-full animate-spin"></div>
                <p className="text-sm font-medium text-[#6B5D53] animate-pulse">Loading amazing products...</p>
              </div>
            )}

            {/* End Message */}
            {!loading && items.length > 0 && items.length >= totalCount && (
              <div className="text-center py-12">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-[999px] bg-[#F6EFE2] text-[#A6472E] text-sm font-medium border border-[#E5D9C3]">
                  ✨ You’ve reached the end of the collection
                </div>
              </div>
            )}
          </main>
        </div>

        {/* Mobile Filter Offcanvas Drawer with Backdrop Animation */}
        {isMobileFilterOpen && (
          <div className="fixed inset-0 z-50 md:hidden flex">
            {/* Backdrop */}
            <div 
              className="fixed inset-0 bg-[#1A1310]/60 backdrop-blur-sm transition-opacity"
              onClick={() => setIsMobileFilterOpen(false)}
            />

            {/* Drawer Content */}
            <div className="relative ml-auto w-full max-w-xs bg-[#FFFDF8] h-full shadow-[0_10px_30px_rgba(74,47,30,0.2)] flex flex-col z-10 overflow-hidden">
              <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5D9C3] bg-[#F6EFE2]">
                <h5 className="font-bold text-[#4B3226] flex items-center gap-2">
                  <FiFilter className="text-[#A6472E]" /> Filters
                </h5>
                <button
                  type="button"
                  onClick={() => setIsMobileFilterOpen(false)}
                  className="p-2 text-[#6B5D53] hover:text-[#2E211A] rounded-full hover:bg-[#FFFDF8] transition-colors"
                >
                  <FiX size={20} />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-6">
                <Filtersidebar
                  filterData={filterData}
                  categories={categories}
                  filters={filters}
                  onFilterChange={handleFilterChange}
                  onCheckboxChange={handleCheckboxChange}
                  priceRange={priceRange}
                  onPriceChange={handlePriceChange}
                  onApply={handleApplyFilters}
                />
              </div>
            </div>
          </div>
        )}
      </section>
    </div>
    </>
  );
}

export default ListPage;